import { createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import {
  CheckCircle2,
  CircleAlert,
  CircleSlash,
  Globe,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  ShieldCheck,
  Trash2,
  X,
} from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { useCurrentUser } from "@/lib/hooks/auth";
import {
  useDeleteDomainAuthMode,
  useDomainAuthModes,
  useSsoStatus,
  useUpdateKeycloakConfig,
  useUpdateSamlConfig,
  useUploadSamlIdpMetadata,
  useUpsertDomainAuthMode,
} from "@/lib/hooks/admin";
import { useResetMfaErrorOnVerify } from "@/lib/hooks/mfa-verify-prompt";
import { cn } from "@/lib/utils";
import type {
  DomainAuthMode,
  IdpHealthcheck,
  KeycloakConfigUpdateInput,
  KeycloakStatus,
  SamlConfigUpdateInput,
  SamlStatus,
} from "@/lib/schemas/admin";
import { DOMAIN_AUTH_MODES } from "@/lib/schemas/admin";

export const Route = createFileRoute("/_app/settings_/system/sso")({
  component: SsoStatusPage,
});

const JIT_ROLE_OPTIONS = ["peer_read", "customer", "reseller", "admin_read_only", "admin"] as const;

function csvToArray(csv: string): string[] {
  return csv
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function arrayToCsv(arr: string[]): string {
  return arr.join(", ");
}

function SsoStatusPage(): JSX.Element {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const query = useSsoStatus();
  const [editKeycloak, setEditKeycloak] = useState(false);
  const [editSaml, setEditSaml] = useState(false);

  // Phase 11.9: superuser-only. BE returns 403; ma renderizziamo gate FE
  // anche per evitare la query inutile + messaggio dedicato.
  if (me.data && me.data.role !== "superuser") {
    return (
      <div className="space-y-3">
        <h1 className="text-3xl font-semibold tracking-tight">
          {t("sso_status.title")}
        </h1>
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {t("sso_status.superuser_only")}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">
            {t("sso_status.title")}
          </h1>
          <p className="text-sm text-muted-foreground">{t("sso_status.subtitle")}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void query.refetch()}
          disabled={query.isFetching}
        >
          <RefreshCw
            className={cn("mr-1.5 size-3.5", query.isFetching && "animate-spin")}
            aria-hidden="true"
          />
          {t("sso_status.refresh")}
        </Button>
      </header>

      {query.error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {t("sso_status.load_error")} — {query.error.message}
        </div>
      )}

      {query.data && (
        <>
          <p className="text-xs text-muted-foreground">
            {t("sso_status.evaluated_at", {
              env: query.data.environment,
              at: format(new Date(query.data.evaluated_at), "PPpp"),
            })}
          </p>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {editKeycloak ? (
              <KeycloakEditForm
                data={query.data.keycloak}
                onClose={() => setEditKeycloak(false)}
              />
            ) : (
              <KeycloakCard
                data={query.data.keycloak}
                onEdit={() => setEditKeycloak(true)}
              />
            )}
            {editSaml ? (
              <SamlEditForm data={query.data.saml} onClose={() => setEditSaml(false)} />
            ) : (
              <SamlCard data={query.data.saml} onEdit={() => setEditSaml(true)} />
            )}
          </div>
          <p className="text-xs text-muted-foreground">{t("sso_status.edit_hint_rw")}</p>
          <DomainAuthModeCard />
        </>
      )}
    </div>
  );
}

/* ──────────────────────────────────── Domain auth-mode overrides (D4) */

function DomainAuthModeCard(): JSX.Element {
  const { t } = useTranslation();
  const listQuery = useDomainAuthModes();
  const upsert = useUpsertDomainAuthMode();
  const remove = useDeleteDomainAuthMode();

  const [domain, setDomain] = useState("");
  const [mode, setMode] = useState<DomainAuthMode>("federated");
  const [error, setError] = useState<string | null>(null);

  const trimmed = domain.trim();
  const busy = upsert.isPending || remove.isPending;

  const onAdd = (): void => {
    setError(null);
    if (!trimmed) {
      setError(t("sso_status.domain_modes.domain_required"));
      return;
    }
    upsert.mutate(
      { domain: trimmed, input: { mode } },
      {
        onSuccess: () => {
          setDomain("");
          setMode("federated");
        },
        onError: (err) => setError(formatApiError(err).join(" — ")),
      },
    );
  };

  const items = listQuery.data?.items ?? [];

  return (
    <section className="space-y-3 rounded-md border border-border bg-card p-4">
      <header className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <Globe className="size-5 text-primary" aria-hidden="true" />
          {t("sso_status.domain_modes.title")}
        </h2>
      </header>
      <p className="text-xs text-muted-foreground">
        {t("sso_status.domain_modes.hint")}
      </p>

      {/* Add form */}
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 space-y-1" style={{ minWidth: "12rem" }}>
          <Label htmlFor="dam_domain">{t("sso_status.domain_modes.domain")}</Label>
          <Input
            id="dam_domain"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="example.com"
            disabled={busy}
            autoComplete="off"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="dam_mode">{t("sso_status.domain_modes.mode")}</Label>
          <select
            id="dam_mode"
            value={mode}
            onChange={(e) => setMode(e.target.value as DomainAuthMode)}
            disabled={busy}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            {DOMAIN_AUTH_MODES.map((m) => (
              <option key={m} value={m}>
                {t(`sso_status.domain_modes.mode_${m}`)}
              </option>
            ))}
          </select>
        </div>
        <Button type="button" size="sm" onClick={onAdd} disabled={busy}>
          <Plus className="mr-1.5 size-3.5" aria-hidden="true" />
          {t("sso_status.domain_modes.add")}
        </Button>
      </div>

      {error && (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      )}

      {/* Overrides list */}
      {listQuery.isLoading ? (
        <p className="text-xs text-muted-foreground">{t("sso_status.domain_modes.loading")}</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t("sso_status.domain_modes.empty")}</p>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border">
          {items.map((item) => (
            <li
              key={item.domain}
              className="flex items-center justify-between gap-2 px-3 py-2 text-sm"
            >
              <div className="min-w-0">
                <span className="font-mono">{item.domain}</span>
                {item.note && (
                  <span className="ml-2 text-xs text-muted-foreground">{item.note}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    "rounded px-1.5 py-0.5 text-xs font-medium",
                    item.mode === "federated"
                      ? "bg-primary/10 text-primary"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  {t(`sso_status.domain_modes.mode_${item.mode}`)}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => remove.mutate(item.domain)}
                  disabled={busy}
                  aria-label={t("sso_status.domain_modes.delete")}
                >
                  <Trash2 className="size-3.5 text-destructive" aria-hidden="true" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ────────────────────────────────────────────────────────── Keycloak card */

function KeycloakCard({
  data,
  onEdit,
}: {
  data: KeycloakStatus;
  onEdit: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <section className="space-y-3 rounded-md border border-border bg-card p-4">
      <header className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <ShieldCheck className="size-5 text-primary" aria-hidden="true" />
          {t("sso_status.keycloak.title")}
        </h2>
        <div className="flex items-center gap-2">
          <SourceBadge source={data.source} />
          <EnabledBadge enabled={data.enabled} />
          <Button type="button" variant="outline" size="sm" onClick={onEdit}>
            <Pencil className="mr-1.5 size-3.5" aria-hidden="true" />
            {t("sso_status.edit")}
          </Button>
        </div>
      </header>

      <dl className="grid grid-cols-1 gap-2 text-xs">
        <Field label={t("sso_status.keycloak.issuer")} value={data.issuer} mono />
        <Field label={t("sso_status.keycloak.realm")} value={data.realm} mono />
        <Field
          label={t("sso_status.keycloak.client_id")}
          value={data.client_id}
          mono
        />
        <Field
          label={t("sso_status.keycloak.client_secret")}
          value={
            data.client_secret_configured
              ? t("sso_status.configured")
              : t("sso_status.not_configured")
          }
          tone={data.client_secret_configured ? "ok" : "warn"}
        />
        <Field
          label={t("sso_status.keycloak.audience")}
          value={data.audience}
          mono
        />
        <Field
          label={t("sso_status.keycloak.jwks_cache_ttl")}
          value={`${data.jwks_cache_ttl_seconds}s`}
        />
      </dl>

      <div className="border-t border-border pt-2">
        <h3 className="text-xs font-semibold text-muted-foreground">
          {t("sso_status.keycloak.jit_title")}
        </h3>
        <dl className="mt-1 grid grid-cols-1 gap-1 text-xs">
          <Field
            label={t("sso_status.keycloak.jit_enabled")}
            value={
              data.jit.enabled
                ? t("sso_status.enabled")
                : t("sso_status.disabled")
            }
            tone={data.jit.enabled ? "ok" : "muted"}
          />
          <Field
            label={t("sso_status.keycloak.jit_default_role")}
            value={data.jit.default_role}
            mono
          />
          <Field
            label={t("sso_status.keycloak.jit_allowed_domains")}
            value={
              data.jit.allowed_email_domains.length > 0
                ? data.jit.allowed_email_domains.join(", ")
                : t("sso_status.empty_list")
            }
            mono={data.jit.allowed_email_domains.length > 0}
            tone={
              data.jit.enabled && data.jit.allowed_email_domains.length === 0
                ? "warn"
                : undefined
            }
          />
          <Field
            label={t("sso_status.keycloak.jit_role_mapping")}
            value={
              Object.keys(data.jit.role_mapping).length > 0
                ? Object.entries(data.jit.role_mapping)
                    .map(([k, v]) => `${k} → ${v}`)
                    .join(", ")
                : t("sso_status.empty_list")
            }
            mono={Object.keys(data.jit.role_mapping).length > 0}
          />
        </dl>
      </div>

      <div className="border-t border-border pt-2">
        <h3 className="text-xs font-semibold text-muted-foreground">
          {t("sso_status.keycloak.provisioning_title")}
        </h3>
        <dl className="mt-1 grid grid-cols-1 gap-1 text-xs">
          <Field
            label={t("sso_status.keycloak.provisioning_enabled")}
            value={data.provisioning_enabled ? t("sso_status.enabled") : t("sso_status.disabled")}
            tone={data.provisioning_enabled ? "ok" : "muted"}
          />
          <Field
            label={t("sso_status.keycloak.admin_client_id")}
            value={data.admin_client_id || t("sso_status.not_configured")}
            mono={Boolean(data.admin_client_id)}
            tone={data.provisioning_enabled && !data.admin_client_id ? "warn" : undefined}
          />
          <Field
            label={t("sso_status.keycloak.admin_client_secret")}
            value={
              data.admin_client_secret_configured
                ? t("sso_status.configured")
                : t("sso_status.not_configured")
            }
            tone={data.admin_client_secret_configured ? "ok" : data.provisioning_enabled ? "warn" : "muted"}
          />
        </dl>
      </div>

      {data.healthcheck && (
        <div className="border-t border-border pt-2">
          <h3 className="text-xs font-semibold text-muted-foreground">
            {t("sso_status.healthcheck")}
          </h3>
          <div className="mt-1 space-y-1 text-xs">
            <HealthcheckRow
              label={t("sso_status.keycloak.jwks_endpoint")}
              hc={data.healthcheck.issuer_reachable}
            />
            {data.healthcheck.jwks_keys_count !== null &&
              data.healthcheck.jwks_keys_count !== undefined && (
                <Field
                  label={t("sso_status.keycloak.jwks_keys_count")}
                  value={String(data.healthcheck.jwks_keys_count)}
                  tone={
                    data.healthcheck.jwks_keys_count > 0 ? "ok" : "warn"
                  }
                />
              )}
          </div>
        </div>
      )}
    </section>
  );
}

/* ──────────────────────────────────────────────────── Keycloak edit form */

interface KeycloakFormValues {
  issuer: string;
  realm: string;
  client_id: string;
  audience: string;
  jwks_cache_ttl_seconds: number;
  client_secret: string;
  jit_enabled: boolean;
  jit_allowed_email_domains: string;
  jit_default_role: string;
  jit_role_mapping: string;
  provisioning_enabled: boolean;
  admin_client_id: string;
  admin_client_secret: string;
  admin_org_cache_ttl_seconds: number;
}

function KeycloakEditForm({
  data,
  onClose,
}: {
  data: KeycloakStatus;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const mutation = useUpdateKeycloakConfig();
  useResetMfaErrorOnVerify(mutation);
  const [jsonError, setJsonError] = useState<string | null>(null);

  const form = useForm<KeycloakFormValues>({
    defaultValues: {
      issuer: data.issuer,
      realm: data.realm,
      client_id: data.client_id,
      audience: data.audience,
      jwks_cache_ttl_seconds: data.jwks_cache_ttl_seconds,
      client_secret: "",
      jit_enabled: data.jit.enabled,
      jit_allowed_email_domains: arrayToCsv(data.jit.allowed_email_domains),
      jit_default_role: data.jit.default_role,
      jit_role_mapping: JSON.stringify(data.jit.role_mapping ?? {}),
      provisioning_enabled: data.provisioning_enabled,
      admin_client_id: data.admin_client_id,
      admin_client_secret: "",
      admin_org_cache_ttl_seconds: data.admin_org_cache_ttl_seconds,
    },
  });

  async function onSubmit(values: KeycloakFormValues): Promise<void> {
    setJsonError(null);
    let mapping: Record<string, string>;
    try {
      mapping = JSON.parse(values.jit_role_mapping.trim() || "{}");
    } catch {
      setJsonError(t("sso_status.errors.role_mapping_json"));
      return;
    }
    const payload: KeycloakConfigUpdateInput = {
      issuer: values.issuer.trim(),
      realm: values.realm.trim(),
      client_id: values.client_id.trim(),
      audience: values.audience.trim(),
      jwks_cache_ttl_seconds: Number(values.jwks_cache_ttl_seconds),
      client_secret: values.client_secret.trim() === "" ? null : values.client_secret,
      jit_enabled: values.jit_enabled,
      jit_allowed_email_domains: csvToArray(values.jit_allowed_email_domains),
      jit_default_role: values.jit_default_role as KeycloakConfigUpdateInput["jit_default_role"],
      jit_role_mapping: mapping,
      provisioning_enabled: values.provisioning_enabled,
      admin_client_id: values.admin_client_id.trim(),
      admin_client_secret: values.admin_client_secret.trim() === "" ? null : values.admin_client_secret,
      admin_org_cache_ttl_seconds: Number(values.admin_org_cache_ttl_seconds),
    };
    try {
      await mutation.mutateAsync(payload);
      onClose();
    } catch {
      // surfaced via mutation.error
    }
  }

  const jitEnabled = form.watch("jit_enabled");
  const provisioningEnabled = form.watch("provisioning_enabled");

  return (
    <section className="space-y-3 rounded-md border border-primary/40 bg-card p-4">
      <header className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <ShieldCheck className="size-5 text-primary" aria-hidden="true" />
          {t("sso_status.keycloak.edit_title")}
        </h2>
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          <X className="mr-1.5 size-3.5" aria-hidden="true" />
          {t("common.cancel")}
        </Button>
      </header>

      <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)} className="space-y-3 text-xs">
        <TextField id="kc_issuer" label={t("sso_status.keycloak.issuer")} reg={form.register("issuer")} disabled={mutation.isPending} placeholder="https://kc.example.com/realms/dpanel" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <TextField id="kc_realm" label={t("sso_status.keycloak.realm")} reg={form.register("realm")} disabled={mutation.isPending} />
          <TextField id="kc_client_id" label={t("sso_status.keycloak.client_id")} reg={form.register("client_id")} disabled={mutation.isPending} />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <TextField id="kc_audience" label={t("sso_status.keycloak.audience")} reg={form.register("audience")} disabled={mutation.isPending} />
          <div className="space-y-1">
            <Label htmlFor="kc_ttl" className="text-xs font-medium">
              {t("sso_status.keycloak.jwks_cache_ttl")}
            </Label>
            <Input id="kc_ttl" type="number" min={60} max={3600} disabled={mutation.isPending} {...form.register("jwks_cache_ttl_seconds", { valueAsNumber: true })} />
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="kc_secret" className="text-xs font-medium">
            {t("sso_status.keycloak.client_secret")}
          </Label>
          <Input
            id="kc_secret"
            type="password"
            autoComplete="off"
            placeholder={
              data.client_secret_configured
                ? t("sso_status.secret_keep_placeholder")
                : t("sso_status.secret_set_placeholder")
            }
            disabled={mutation.isPending}
            {...form.register("client_secret")}
          />
          <p className="text-[10px] text-muted-foreground">{t("sso_status.secret_hint")}</p>
        </div>

        <JitFieldset
          prefix="kc"
          enabled={jitEnabled}
          register={form.register}
          setEnabled={(v) => form.setValue("jit_enabled", v)}
          disabled={mutation.isPending}
          jsonError={jsonError}
        />

        {/* ADR-0100 D7-bis: hub-realm provisioning (admin REST) */}
        <fieldset className="space-y-2 rounded-md border border-border p-3">
          <legend className="px-1 text-xs font-semibold text-muted-foreground">
            {t("sso_status.keycloak.provisioning_title")}
          </legend>
          <p className="text-[10px] text-muted-foreground">{t("sso_status.keycloak.provisioning_hint")}</p>
          <label className="flex items-center gap-2">
            <Checkbox
              checked={provisioningEnabled}
              onCheckedChange={(v) => form.setValue("provisioning_enabled", v === true)}
              disabled={mutation.isPending}
            />
            <span>{t("sso_status.keycloak.provisioning_enabled")}</span>
          </label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <TextField
              id="kc_admin_client_id"
              label={t("sso_status.keycloak.admin_client_id")}
              reg={form.register("admin_client_id")}
              disabled={mutation.isPending}
              placeholder="dpanel-admin"
            />
            <div className="space-y-1">
              <Label htmlFor="kc_admin_ttl" className="text-xs font-medium">
                {t("sso_status.keycloak.admin_org_cache_ttl")}
              </Label>
              <Input
                id="kc_admin_ttl"
                type="number"
                min={30}
                max={3600}
                disabled={mutation.isPending}
                {...form.register("admin_org_cache_ttl_seconds", { valueAsNumber: true })}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="kc_admin_secret" className="text-xs font-medium">
              {t("sso_status.keycloak.admin_client_secret")}
            </Label>
            <Input
              id="kc_admin_secret"
              type="password"
              autoComplete="off"
              placeholder={
                data.admin_client_secret_configured
                  ? t("sso_status.secret_keep_placeholder")
                  : t("sso_status.secret_set_placeholder")
              }
              disabled={mutation.isPending}
              {...form.register("admin_client_secret")}
            />
            <p className="text-[10px] text-muted-foreground">{t("sso_status.secret_hint")}</p>
          </div>
        </fieldset>

        <FormFooter mutation={mutation} />
      </form>
    </section>
  );
}

/* ────────────────────────────────────────────────────────── SAML card */

function GoogleSetupPanel({ data }: { data: SamlStatus }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="rounded-md border border-dashed border-border bg-muted/30 p-3">
      <p className="mb-2 text-xs font-semibold text-muted-foreground">
        {t("sso_status.saml.google_setup_title")}
      </p>
      <dl className="grid grid-cols-1 gap-2 text-xs">
        <Field label={t("sso_status.saml.google_acs_url")} value={data.sp_acs_url || "—"} mono={Boolean(data.sp_acs_url)} />
        <Field label={t("sso_status.saml.google_entity_id")} value={data.sp_entity_id || "—"} mono={Boolean(data.sp_entity_id)} />
        <Field label={t("sso_status.saml.google_start_url")} value={data.start_url || "—"} mono={Boolean(data.start_url)} />
        <Field label={t("sso_status.saml.google_name_id_format")} value={data.name_id_format || "—"} mono={Boolean(data.name_id_format)} />
        <Field label={t("sso_status.saml.google_name_id")} value={data.name_id_hint || "—"} />
      </dl>
    </div>
  );
}

function IdpMetadataUpload({ onUploaded }: { onUploaded: () => void }): JSX.Element {
  const { t } = useTranslation();
  const upload = useUploadSamlIdpMetadata();
  return (
    <div className="space-y-1 rounded-md border border-dashed border-border bg-muted/30 p-3">
      <Label htmlFor="saml_idp_metadata" className="text-xs font-medium">
        {t("sso_status.saml.idp_metadata_upload")}
      </Label>
      <input
        id="saml_idp_metadata"
        type="file"
        accept=".xml,text/xml,application/xml"
        disabled={upload.isPending}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) upload.mutate(f, { onSuccess: () => onUploaded() });
          e.target.value = "";
        }}
        className="block w-full text-xs file:mr-2 file:rounded file:border file:border-input file:bg-background file:px-2 file:py-1"
      />
      <p className="text-[11px] text-muted-foreground">{t("sso_status.saml.idp_metadata_upload_hint")}</p>
      {upload.isPending && (
        <p className="text-[11px] text-muted-foreground">{t("sso_status.saml.idp_metadata_uploading")}</p>
      )}
      {upload.error && <p className="text-[11px] text-destructive">{formatApiError(upload.error)}</p>}
    </div>
  );
}

function SamlCard({
  data,
  onEdit,
}: {
  data: SamlStatus;
  onEdit: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const certExpiring =
    data.idp_cert_not_after &&
    new Date(data.idp_cert_not_after).getTime() - Date.now() < 30 * 24 * 3600 * 1000;

  return (
    <section className="space-y-3 rounded-md border border-border bg-card p-4">
      <header className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <ShieldCheck className="size-5 text-primary" aria-hidden="true" />
          {t("sso_status.saml.title")}
        </h2>
        <div className="flex items-center gap-2">
          <SourceBadge source={data.source} />
          <EnabledBadge enabled={data.enabled} />
          <Button type="button" variant="outline" size="sm" onClick={onEdit}>
            <Pencil className="mr-1.5 size-3.5" aria-hidden="true" />
            {t("sso_status.edit")}
          </Button>
        </div>
      </header>

      <GoogleSetupPanel data={data} />

      <dl className="grid grid-cols-1 gap-2 text-xs">
        <Field
          label={t("sso_status.saml.sp_entity_id")}
          value={data.sp_entity_id || t("sso_status.not_configured")}
          mono={Boolean(data.sp_entity_id)}
        />
        <Field
          label={t("sso_status.saml.sp_acs_url")}
          value={data.sp_acs_url || t("sso_status.not_configured")}
          mono={Boolean(data.sp_acs_url)}
        />
        <Field
          label={t("sso_status.saml.idp_entity_id")}
          value={data.idp_entity_id || t("sso_status.not_configured")}
          mono={Boolean(data.idp_entity_id)}
        />
        <Field
          label={t("sso_status.saml.idp_sso_url")}
          value={data.idp_sso_url || t("sso_status.not_configured")}
          mono={Boolean(data.idp_sso_url)}
        />
        <Field
          label={t("sso_status.saml.idp_cert")}
          value={
            data.idp_cert_configured
              ? t("sso_status.configured")
              : t("sso_status.not_configured")
          }
          tone={data.idp_cert_configured ? "ok" : data.enabled ? "warn" : "muted"}
        />
        {data.idp_cert_subject && (
          <Field
            label={t("sso_status.saml.idp_cert_subject")}
            value={data.idp_cert_subject}
            mono
          />
        )}
        {data.idp_cert_not_after && (
          <Field
            label={t("sso_status.saml.idp_cert_expires")}
            value={format(new Date(data.idp_cert_not_after), "PP")}
            tone={certExpiring ? "warn" : "ok"}
          />
        )}
        <Field
          label={t("sso_status.saml.sp_signing")}
          value={
            data.sp_signing_configured
              ? t("sso_status.configured")
              : t("sso_status.not_configured_optional")
          }
          tone={data.sp_signing_configured ? "ok" : "muted"}
        />
        <Field
          label={t("sso_status.saml.encrypted_assertions")}
          value={
            data.want_assertions_encrypted
              ? t("sso_status.enabled")
              : t("sso_status.disabled")
          }
          tone={data.want_assertions_encrypted ? "ok" : "muted"}
        />
        <Field
          label={t("sso_status.saml.allowed_domains")}
          value={
            data.allowed_email_domains.length > 0
              ? data.allowed_email_domains.join(", ")
              : t("sso_status.empty_list")
          }
          mono={data.allowed_email_domains.length > 0}
          tone={
            data.enabled && data.allowed_email_domains.length === 0
              ? "warn"
              : undefined
          }
        />
      </dl>

      {data.jit && (
        <div className="border-t border-border pt-2">
          <h3 className="text-xs font-semibold text-muted-foreground">
            {t("sso_status.saml.jit_title")}
          </h3>
          <dl className="mt-1 grid grid-cols-1 gap-1 text-xs">
            <Field
              label={t("sso_status.keycloak.jit_enabled")}
              value={data.jit.enabled ? t("sso_status.enabled") : t("sso_status.disabled")}
              tone={data.jit.enabled ? "ok" : "muted"}
            />
            <Field
              label={t("sso_status.keycloak.jit_default_role")}
              value={data.jit.default_role}
              mono
            />
            <Field
              label={t("sso_status.keycloak.jit_allowed_domains")}
              value={
                data.jit.allowed_email_domains.length > 0
                  ? data.jit.allowed_email_domains.join(", ")
                  : t("sso_status.empty_list")
              }
              mono={data.jit.allowed_email_domains.length > 0}
              tone={
                data.jit.enabled && data.jit.allowed_email_domains.length === 0
                  ? "warn"
                  : undefined
              }
            />
          </dl>
        </div>
      )}

      {data.healthcheck && (
        <div className="border-t border-border pt-2">
          <h3 className="text-xs font-semibold text-muted-foreground">
            {t("sso_status.healthcheck")}
          </h3>
          <div className="mt-1 text-xs">
            <HealthcheckRow
              label={t("sso_status.saml.sso_url_probe")}
              hc={data.healthcheck}
            />
          </div>
        </div>
      )}
    </section>
  );
}

/* ──────────────────────────────────────────────────────── SAML edit form */

interface SamlFormValues {
  enabled: boolean;
  sp_entity_id: string;
  sp_acs_url: string;
  idp_entity_id: string;
  idp_sso_url: string;
  idp_x509_cert: string;
  sp_x509_cert: string;
  sp_private_key: string;
  allowed_email_domains: string;
  want_assertions_encrypted: boolean;
  jit_enabled: boolean;
  jit_allowed_email_domains: string;
  jit_default_role: string;
  jit_role_mapping: string;
}

function SamlEditForm({
  data,
  onClose,
}: {
  data: SamlStatus;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const mutation = useUpdateSamlConfig();
  useResetMfaErrorOnVerify(mutation);
  const [jsonError, setJsonError] = useState<string | null>(null);

  const form = useForm<SamlFormValues>({
    defaultValues: {
      enabled: data.enabled,
      sp_entity_id: data.sp_entity_id,
      sp_acs_url: data.sp_acs_url,
      idp_entity_id: data.idp_entity_id,
      idp_sso_url: data.idp_sso_url,
      idp_x509_cert: "",
      sp_x509_cert: "",
      sp_private_key: "",
      allowed_email_domains: arrayToCsv(data.allowed_email_domains),
      want_assertions_encrypted: data.want_assertions_encrypted,
      jit_enabled: data.jit?.enabled ?? false,
      jit_allowed_email_domains: arrayToCsv(data.jit?.allowed_email_domains ?? []),
      jit_default_role: data.jit?.default_role ?? "peer_read",
      jit_role_mapping: JSON.stringify(data.jit?.role_mapping ?? {}),
    },
  });

  async function onSubmit(values: SamlFormValues): Promise<void> {
    setJsonError(null);
    let mapping: Record<string, string>;
    try {
      mapping = JSON.parse(values.jit_role_mapping.trim() || "{}");
    } catch {
      setJsonError(t("sso_status.errors.role_mapping_json"));
      return;
    }
    const payload: SamlConfigUpdateInput = {
      enabled: values.enabled,
      sp_entity_id: values.sp_entity_id.trim(),
      sp_acs_url: values.sp_acs_url.trim(),
      idp_entity_id: values.idp_entity_id.trim(),
      idp_sso_url: values.idp_sso_url.trim(),
      idp_x509_cert: values.idp_x509_cert.trim(),
      sp_x509_cert: values.sp_x509_cert.trim(),
      sp_private_key: values.sp_private_key.trim() === "" ? null : values.sp_private_key,
      allowed_email_domains: csvToArray(values.allowed_email_domains),
      want_assertions_encrypted: values.want_assertions_encrypted,
      jit_enabled: values.jit_enabled,
      jit_allowed_email_domains: csvToArray(values.jit_allowed_email_domains),
      jit_default_role: values.jit_default_role as SamlConfigUpdateInput["jit_default_role"],
      jit_role_mapping: mapping,
    };
    try {
      await mutation.mutateAsync(payload);
      onClose();
    } catch {
      // surfaced via mutation.error
    }
  }

  const enabled = form.watch("enabled");
  const jitEnabled = form.watch("jit_enabled");
  const wantEnc = form.watch("want_assertions_encrypted");

  return (
    <section className="space-y-3 rounded-md border border-primary/40 bg-card p-4">
      <header className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <ShieldCheck className="size-5 text-primary" aria-hidden="true" />
          {t("sso_status.saml.edit_title")}
        </h2>
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          <X className="mr-1.5 size-3.5" aria-hidden="true" />
          {t("common.cancel")}
        </Button>
      </header>

      <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)} className="space-y-3 text-xs">
        <CheckRow id="saml_enabled" label={t("sso_status.saml.field_enabled")} checked={enabled} onChange={(v) => form.setValue("enabled", v)} disabled={mutation.isPending} />

        <GoogleSetupPanel data={data} />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <TextField id="saml_sp_entity" label={t("sso_status.saml.sp_entity_id")} reg={form.register("sp_entity_id")} disabled={mutation.isPending} />
          <TextField id="saml_sp_acs" label={t("sso_status.saml.sp_acs_url")} reg={form.register("sp_acs_url")} disabled={mutation.isPending} />
        </div>
        <IdpMetadataUpload onUploaded={onClose} />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <TextField id="saml_idp_entity" label={t("sso_status.saml.idp_entity_id")} reg={form.register("idp_entity_id")} disabled={mutation.isPending} />
          <TextField id="saml_idp_sso" label={t("sso_status.saml.idp_sso_url")} reg={form.register("idp_sso_url")} disabled={mutation.isPending} />
        </div>

        <CertField
          id="saml_idp_cert"
          label={t("sso_status.saml.idp_cert_pem")}
          hint={
            data.idp_cert_configured
              ? t("sso_status.saml.idp_cert_keep_hint")
              : t("sso_status.saml.idp_cert_set_hint")
          }
          value={form.watch("idp_x509_cert")}
          onChange={(v) => form.setValue("idp_x509_cert", v)}
          disabled={mutation.isPending}
        />

        <CheckRow id="saml_enc" label={t("sso_status.saml.field_encrypted")} checked={wantEnc} onChange={(v) => form.setValue("want_assertions_encrypted", v)} disabled={mutation.isPending} hint={t("sso_status.saml.field_encrypted_hint")} />

        <CertField
          id="saml_sp_key"
          label={t("sso_status.saml.sp_private_key")}
          hint={t("sso_status.saml.sp_private_key_hint")}
          value={form.watch("sp_private_key")}
          onChange={(v) => form.setValue("sp_private_key", v)}
          disabled={mutation.isPending}
          secret
        />
        <CertField
          id="saml_sp_cert"
          label={t("sso_status.saml.sp_cert_pem")}
          hint={t("sso_status.saml.sp_cert_hint")}
          value={form.watch("sp_x509_cert")}
          onChange={(v) => form.setValue("sp_x509_cert", v)}
          disabled={mutation.isPending}
        />

        <div className="space-y-1">
          <Label htmlFor="saml_domains" className="text-xs font-medium">
            {t("sso_status.saml.allowed_domains")}
          </Label>
          <Input id="saml_domains" placeholder="dinova.one, dinova.cloud" disabled={mutation.isPending} {...form.register("allowed_email_domains")} />
        </div>

        <JitFieldset
          prefix="saml"
          enabled={jitEnabled}
          register={form.register}
          setEnabled={(v) => form.setValue("jit_enabled", v)}
          disabled={mutation.isPending}
          jsonError={jsonError}
          jitNote={t("sso_status.saml.jit_note")}
        />

        <FormFooter mutation={mutation} />
      </form>
    </section>
  );
}

/* ────────────────────────────────────────────────────────── shared bits */

function JitFieldset(props: {
  prefix: string;
  enabled: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: any;
  setEnabled: (v: boolean) => void;
  disabled: boolean;
  jsonError: string | null;
  jitNote?: string;
}): JSX.Element {
  const { t } = useTranslation();
  const { prefix, enabled, register, setEnabled, disabled, jsonError, jitNote } = props;
  return (
    <div className="space-y-2 border-t border-border pt-3">
      <h3 className="text-xs font-semibold text-muted-foreground">
        {t("sso_status.jit.title")}
      </h3>
      {jitNote && (
        <p className="rounded-md border border-warning/30 bg-warning/5 p-2 text-[10px] text-muted-foreground">
          {jitNote}
        </p>
      )}
      <CheckRow id={`${prefix}_jit_enabled`} label={t("sso_status.jit.enabled")} checked={enabled} onChange={setEnabled} disabled={disabled} />
      <div className="space-y-1">
        <Label htmlFor={`${prefix}_jit_domains`} className="text-xs font-medium">
          {t("sso_status.jit.allowed_domains")}
        </Label>
        <Input id={`${prefix}_jit_domains`} placeholder="dinova.one" disabled={disabled} {...register("jit_allowed_email_domains")} />
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${prefix}_jit_role`} className="text-xs font-medium">
          {t("sso_status.jit.default_role")}
        </Label>
        <select
          id={`${prefix}_jit_role`}
          disabled={disabled}
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
          {...register("jit_default_role")}
        >
          {JIT_ROLE_OPTIONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${prefix}_jit_mapping`} className="text-xs font-medium">
          {t("sso_status.jit.role_mapping")}
        </Label>
        <textarea
          id={`${prefix}_jit_mapping`}
          rows={2}
          disabled={disabled}
          className="flex w-full rounded-md border border-input bg-background px-3 py-1.5 font-mono text-xs shadow-sm"
          placeholder='{"dpanel-admins": "admin"}'
          {...register("jit_role_mapping")}
        />
        <p className="text-[10px] text-muted-foreground">{t("sso_status.jit.role_mapping_hint")}</p>
        {jsonError && <p className="text-xs text-destructive">{jsonError}</p>}
      </div>
    </div>
  );
}

function TextField({
  id,
  label,
  reg,
  disabled,
  placeholder,
}: {
  id: string;
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reg: any;
  disabled: boolean;
  placeholder?: string;
}): JSX.Element {
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-xs font-medium">
        {label}
      </Label>
      <Input id={id} disabled={disabled} placeholder={placeholder} {...reg} />
    </div>
  );
}

function CertField({
  id,
  label,
  hint,
  value,
  onChange,
  disabled,
  secret,
}: {
  id: string;
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  secret?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  function onFile(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onChange(String(reader.result ?? ""));
    reader.readAsText(file);
  }
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id} className="text-xs font-medium">
          {label}
        </Label>
        <label className="cursor-pointer text-[10px] text-primary hover:underline">
          {t("sso_status.upload_pem")}
          <input type="file" accept=".pem,.crt,.cer,.key,application/x-pem-file,text/plain" className="hidden" onChange={onFile} disabled={disabled} />
        </label>
      </div>
      <textarea
        id={id}
        rows={3}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "flex w-full rounded-md border border-input bg-background px-3 py-1.5 font-mono text-[10px] shadow-sm",
          secret && "tracking-widest",
        )}
        placeholder={secret ? t("sso_status.secret_keep_placeholder") : "-----BEGIN CERTIFICATE-----"}
      />
      <p className="text-[10px] text-muted-foreground">{hint}</p>
    </div>
  );
}

function CheckRow({
  id,
  label,
  checked,
  onChange,
  disabled,
  hint,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled: boolean;
  hint?: string;
}): JSX.Element {
  return (
    <div className="space-y-0.5">
      <div className="flex items-start gap-2">
        <Checkbox
          id={id}
          checked={checked}
          onCheckedChange={(c) => onChange(c === true)}
          disabled={disabled}
          className="mt-0.5"
        />
        <Label htmlFor={id} className="cursor-pointer text-xs font-medium">
          {label}
        </Label>
      </div>
      {hint && <p className="pl-6 text-[10px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

function FormFooter({
  mutation,
}: {
  mutation: { isPending: boolean; error: unknown };
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      {mutation.error != null && (
        <ul className="space-y-0.5 text-xs text-destructive" role="alert">
          {formatApiError(mutation.error as Error).map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
      <div className="flex justify-end">
        <Button type="submit" size="sm" disabled={mutation.isPending} className="gap-1.5">
          {mutation.isPending ? (
            <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
          ) : (
            <Save className="size-3.5" aria-hidden="true" />
          )}
          {t("common.save")}
        </Button>
      </div>
    </>
  );
}

function SourceBadge({ source }: { source: "persisted" | "env" }): JSX.Element {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
      {t(`sso_status.source_${source}`)}
    </span>
  );
}

function EnabledBadge({ enabled }: { enabled: boolean }): JSX.Element {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium",
        enabled
          ? "border-success text-success"
          : "border-muted-foreground text-muted-foreground",
      )}
    >
      {enabled ? (
        <CheckCircle2 className="size-3" aria-hidden="true" />
      ) : (
        <CircleSlash className="size-3" aria-hidden="true" />
      )}
      {enabled ? t("sso_status.enabled") : t("sso_status.disabled")}
    </span>
  );
}

function HealthcheckRow({
  label,
  hc,
}: {
  label: string;
  hc: IdpHealthcheck;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span
        className={cn(
          "inline-flex items-center gap-1 font-mono",
          hc.reachable ? "text-success" : "text-destructive",
        )}
      >
        {hc.reachable ? (
          <CheckCircle2 className="size-3" aria-hidden="true" />
        ) : (
          <CircleAlert className="size-3" aria-hidden="true" />
        )}
        {hc.reachable ? t("sso_status.reachable") : t("sso_status.unreachable")}
        {hc.status_code !== null && hc.status_code !== undefined && ` [${hc.status_code}]`}
        {hc.response_time_ms !== null && hc.response_time_ms !== undefined && ` · ${hc.response_time_ms}ms`}
      </span>
    </div>
  );
}

interface FieldProps {
  label: string;
  value: string;
  mono?: boolean;
  tone?: "ok" | "warn" | "muted";
}

function Field({ label, value, mono, tone }: FieldProps): JSX.Element {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          "break-all text-right",
          mono && "font-mono",
          tone === "ok" && "text-success",
          tone === "warn" && "text-warning",
          tone === "muted" && "text-muted-foreground",
        )}
      >
        {value}
      </dd>
    </div>
  );
}
